// frontend/src/components/DtrOfficeTable.jsx
import { useEffect, useState } from 'react';
import api from '../services/api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { ROLES } from '../config/menuData.js';
import DailyTugReportPage from '../pages/daily-tug-report/index.jsx';

const manilaToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date());
const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString());
const Tick = ({ ok }) => <span className={ok ? 'text-success' : 'text-danger'}>{ok ? 'Saved' : 'Missing'}</span>;

/** Daily tug report summary for a chosen date (dispatch + shore engineering). A
 * dispatcher can open a tug's submitted report to review and correct it. */
export default function DtrOfficeTable() {
  const [date, setDate] = useState(manilaToday);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const { hasRole } = useAuth();
  const canCorrect = hasRole(ROLES.DISPATCHER);
  const [reviewing, setReviewing] = useState(null); // tug row open for correction
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    // Ignore the answer for a date the user has already moved on from.
    let current = true;
    setError('');
    api
      .getDtrList(date)
      .then((d) => current && setRows(d.rows))
      .catch((e) => current && setError(e.message || 'Failed to load reports.'));
    return () => {
      current = false;
    };
  }, [date, refresh]);
  if (reviewing) {
    return (
      <div className="card border-0 shadow-sm" data-testid="dtr-office-review">
        <DailyTugReportPage
          office={{
            tugId: reviewing.tugId,
            date,
            onClose: () => setReviewing(null),
            onSaved: () => setRefresh((n) => n + 1),
          }}
        />
      </div>
    );
  }
  return (
    <div className="card border-0 shadow-sm" data-testid="dtr-office-table">
      <div className="card-header bg-white pt-3 border-bottom-0 d-flex justify-content-between align-items-center">
        <h6 className="mb-0 fw-bold text-uppercase small text-secondary">Daily Tug Reports</h6>
        <input type="date" className="form-control form-control-sm w-auto" value={date} max={manilaToday()} onChange={(e) => setDate(e.target.value)} aria-label="Report date" data-testid="dtr-office-date" />
      </div>
      <div className="card-body table-responsive">
        {error && <div className="alert alert-danger">{error}</div>}
        <table className="table table-sm align-middle mb-0">
          <thead>
            <tr><th>Tug</th><th>Deck</th><th>Engine</th><th className="text-end">Fuel on board (L)</th><th className="text-end">Consumption (L)</th><th className="text-end">Excess (L)</th><th className="text-end">Short (L)</th>{canCorrect && <th aria-label="action" />}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.tugId} data-testid="dtr-office-row" data-tug={r.tugName} data-complete={r.complete}>
                <td className="fw-bold">
                  {r.tugName}
                  {r.corrected && <span className="badge bg-warning text-dark ms-2" data-testid="dtr-corrected">Corrected</span>}
                </td>
                <td><Tick ok={r.deckSaved} /></td>
                <td><Tick ok={r.engineSaved} /></td>
                <td className="text-end">{fmt(r.fuelOnBoard)}</td>
                <td className="text-end">{fmt(r.totalConsumption)}</td>
                <td className="text-end">{r.excess ? fmt(r.excess) : '—'}</td>
                <td className={`text-end ${r.short ? 'text-danger fw-bold' : ''}`}>{r.short ? fmt(r.short) : '—'}</td>
                {canCorrect && (
                  <td className="text-end">
                    {(r.deckSaved || r.engineSaved) && (
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setReviewing(r)} data-testid="dtr-review">
                        Review / correct
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
